/**
 * Calendar days as `YYYY-MM-DD` strings.
 *
 * That string — not a `Date` — is the shape a rental window travels in: it goes
 * into the cart, into localStorage and into the order payload, where a `Date`
 * would serialise to an instant in UTC and come back a day early for anyone
 * west of Greenwich. `Date` exists here only long enough for the calendar to
 * draw it.
 *
 * Which is also why nothing below goes through `Date.parse('2026-03-10')`: that
 * form is defined as UTC midnight, so in a negative offset it renders as the
 * 9th. Every conversion here is built from the local calendar fields instead.
 *
 * "Today" is the browser's own day, matching what react-day-picker highlights.
 * Server-rendered day arithmetic (the team panel) must not use these — it has
 * no browser to ask, and pins `Europe/Warsaw` explicitly.
 */

const pad = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` for a `Date`, read off its LOCAL calendar fields. */
export function toISO(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** The `Date` a calendar day names, at local midnight. Invalid input → null. */
export function fromISO(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  // Rejects the impossible ones ("2026-02-30" rolls forward into March).
  return toISO(date) === iso ? date : null
}

/** Today, as a calendar day. */
export function todayISO(): string {
  return toISO(new Date())
}

/** `days` after `iso`, staying on calendar days (DST never shifts the answer). */
export function addDaysISO(iso: string, days: number): string {
  const date = fromISO(iso)
  if (!date) return iso
  date.setDate(date.getDate() + days)
  return toISO(date)
}

const DAY_MONTH = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'short' })
const DAY_MONTH_TIME = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Warsaw',
})
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

/** `12 mar` — or `12 mar 2027` once the date leaves the current year. */
export function formatDay(iso: string): string {
  const date = fromISO(iso)
  if (!date) return ''
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return (sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(date)
}

/**
 * An instant, as a day and an hour: `18 paź, 14:00`.
 *
 * Pinned to Europe/Warsaw rather than the reader's own zone. The hour is a
 * promise made on FLOU's clock — a performer's working day, a deadline the
 * contract counts — and rendering it in the zone of whoever happens to be
 * reading would quietly move it for anybody abroad.
 */
export function formatDayTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return DAY_MONTH_TIME.format(date)
}
